import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  SchedulePreview,
  SchedulePreviewRequest,
  Task,
  TaskSchedule,
  TaskScheduleCreate,
  TaskScheduleUpdate,
} from "@/api/types";

const KEY = ["task-schedules"] as const;
const KEY_ITEM = (id: string) => ["task-schedule", id] as const;
const KEY_EXECUTIONS = (id: string) =>
  ["task-schedule-executions", id] as const;

export function useTaskSchedules() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => api.GET<TaskSchedule[]>("/task-schedules"),
  });
}

export function useTaskSchedule(id: string) {
  return useQuery({
    queryKey: KEY_ITEM(id),
    queryFn: () => api.GET<TaskSchedule>(`/task-schedules/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateTaskSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: TaskScheduleCreate) =>
      api.POST<TaskSchedule>("/task-schedules", payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUpdateTaskSchedule(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: TaskScheduleUpdate) =>
      api.PATCH<TaskSchedule>(`/task-schedules/${id}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: KEY_ITEM(id) });
    },
  });
}

export function useDeleteTaskSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.DELETE<void>(`/task-schedules/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/** Execucoes passadas = tasks com schedule_id (historico clicavel). */
export function useScheduleExecutions(scheduleId: string) {
  return useQuery({
    queryKey: KEY_EXECUTIONS(scheduleId),
    queryFn: () =>
      api.GET<Task[]>("/tasks", {
        params: { schedule_id: scheduleId },
      }),
    enabled: Boolean(scheduleId),
    refetchInterval: 30_000,
  });
}

/**
 * Preview das proximas execucoes calculado pelo BACKEND (croniter --
 * mesma engine do tick; evita divergencia de dialeto/DST de uma lib
 * cron client-side). Tambem valida a expressao (422 -> isError).
 */
export function useSchedulePreview(
  payload: SchedulePreviewRequest | null,
) {
  return useQuery({
    queryKey: ["task-schedule-preview", payload],
    queryFn: () =>
      api.POST<SchedulePreview>("/task-schedules/preview", payload),
    enabled: payload !== null,
    retry: false,
    staleTime: 60_000,
  });
}

export { KEY as TASK_SCHEDULES_KEY };
